import React from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import LoginPage from './pages/LoginPage';
import EmailVerifyPage from './pages/EmailVerifyPage';
import SignupPage from './pages/SignupPage';
import TermsConditionsPage from './pages/TermsConditionsPage';
import Navbar from './components/Navbar';
import TestPage from './pages/Test';
import DashboardLayout from './layouts/DashboardLayout';
import DocumentEditorLayout from './layouts/DocumentEditorLayout';
import UserDashboardPage from './pages/UserDashboardPage';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { AuthProvider } from './components/AuthProvider';
import { SubscriptionProvider } from './components/SubscriptionProvider';

import './App.css';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import NewPasswordPage from './pages/NewPasswordPage';
import ProfileSettingsPage from './pages/ProfileSettingsPage';
import HelpCentrePage from './pages/HelpCentrePage';
import DocumentsPage from './pages/DocumentsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import AdminLoginPage from './pages/admin/AdminLoginPage';
import AdminForgotPasswordPage from './pages/admin/AdminForgotPasswordPage';
import AccessManagementPage from './pages/admin/AccessManagementPage';
import AdminNewPasswordPage from './pages/admin/AdminNewPasswordPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';
import UserQueriesPage from './pages/admin/UserQueriesPage';
import SubAdminDashboard from './pages/admin/SubAdminDashboard';
import DocumentEditor from './pages/DocumentEditor';
import PageNotFoundPage from './pages/PageNotFoundPage';
import SubscriptionPage from './pages/SubscriptionPage';
import SubscriptionSuccessPage from './pages/SubscriptionSuccessPage';
import SubscriptionCancelPage from './pages/SubscriptionCancelPage';

// Define the router configuration
const router = createBrowserRouter([
	// Public routes
	{ path: "/login", element: <LoginPage /> },
	{ path: "/", element: <LoginPage /> },
	{ path: "/verify-email", element: <EmailVerifyPage /> },
	{ path: "/signup", element: <SignupPage /> },
	{ path: "/forgot-password", element: <ForgotPasswordPage /> },
	{ path: "/new-password", element: <NewPasswordPage /> },
	{
		path: "/terms",
		element: <TermsConditionsPage onBack={() => window.history.back()} />
	},
	{
		path: "/privacy-policy",
		element: <PrivacyPolicyPage onBack={() => window.history.back()} />
	},

	// Subscription success and cancel pages (public)
	{ path: "/subscription/success", element: <SubscriptionSuccessPage /> },
	{ path: "/subscription/cancel", element: <SubscriptionCancelPage /> },

	// Protected single routes
	{
		path: "/document-editor/:id",
		element: (
			<ProtectedRoute>
				<DocumentEditorLayout>
					<DocumentEditor />
				</DocumentEditorLayout>
			</ProtectedRoute>
		)
	},

	// Protected grouped routes under DashboardLayout
	{
		element: (
			<ProtectedRoute>
				<DashboardLayout />
			</ProtectedRoute>
		),
		children: [
			{ path: "/dashboard", element: <UserDashboardPage /> },
			{ path: "/settings", element: <ProfileSettingsPage /> },
			{ path: "/my-documents", element: <DocumentsPage /> },
			{ path: "/help", element: <HelpCentrePage /> },
			{ path: "/test", element: <TestPage /> },
			{ path: "/subscription", element: <SubscriptionPage /> },

			// Admin Access Management was grouped under DashboardLayout in the original
			{ path: "/admin/access-management", element: <AccessManagementPage /> },
			{ path: "/admin/dashboard", element: <AdminDashboard /> },
			{ path: "/admin/settings", element: <AdminSettingsPage /> },
			{ path: "/sub-admin/dashboard", element: <SubAdminDashboard /> },
			{ path: "/sub-admin/user-queries", element: <UserQueriesPage /> },
		]
	},

	// Admin routes (public login/reset)
	{ path: "/admin/login", element: <AdminLoginPage /> },
	{ path: "/admin/forgot-password", element: <AdminForgotPasswordPage /> },
	{ path: "/admin/new-password", element: <AdminNewPasswordPage /> },

	// Demo route for Navbar
	{
		path: "/navbar",
		element: (
			<ProtectedRoute>
				<Navbar />
			</ProtectedRoute>
		)
	},

	// 404 - Catch all unmatched routes
	{ path: "*", element: <PageNotFoundPage /> }
]);

function App() {
	return (
		<AuthProvider>
			<SubscriptionProvider>
				<RouterProvider router={router} />
			</SubscriptionProvider>
		</AuthProvider>
	);
}

export default App;
